import type { BoardState, ChainStep, Node, Pathway, PathwayChange, StrikeResult, Transmutation } from '@/types';
import { HARMONIC_INTERVALS, PathwayType } from '@/types';

/**
 * Wraps a frequency value into the 1-12 range.
 * Frequencies are 1-based (1..12), so we subtract 1, mod 12, add 1.
 */
function wrapFrequency(freq: number): number {
  return ((freq - 1) % 12 + 12) % 12 + 1;
}

/**
 * Computes the pathway modifier based on pathway type and context.
 */
function getPathwayModifier(
  pathway: Pathway,
  sourceNode: Node,
  targetNode: Node,
): number {
  switch (pathway.type) {
    case PathwayType.Clear:
      return 1;
    case PathwayType.Amplifying:
      return 2;
    case PathwayType.Dampening:
      return 0.5;
    case PathwayType.Inverting:
      return -1;
    case PathwayType.Filtering: {
      const threshold = pathway.filterThreshold ?? 0;
      return sourceNode.frequency >= threshold ? 1 : 0;
    }
    case PathwayType.Resonant: {
      const requiredDiff = HARMONIC_INTERVALS[pathway.harmonic] ?? 0;
      const diff = ((sourceNode.frequency - targetNode.frequency) % 12 + 12) % 12;
      const satisfied = diff === requiredDiff || (12 - diff) % 12 === requiredDiff;
      return satisfied ? 1 : 0;
    }
  }
}

/**
 * Determines if traversal is allowed along a pathway from `fromNodeId` to `toNodeId`.
 * For undirected pathways, traversal is always allowed.
 * For directed pathways, traversal is only allowed from source to target.
 */
function canTraverse(pathway: Pathway, fromNodeId: string, toNodeId: string): boolean {
  if (!pathway.directed) {
    return (
      (pathway.source === fromNodeId && pathway.target === toNodeId) ||
      (pathway.target === fromNodeId && pathway.source === toNodeId)
    );
  }
  // Directed: only source → target
  return pathway.source === fromNodeId && pathway.target === toNodeId;
}

/**
 * Gets the node on the other end of a pathway from `fromNodeId`.
 */
function getOtherNode(pathway: Pathway, fromNodeId: string): string | null {
  if (pathway.source === fromNodeId) return pathway.target;
  if (pathway.target === fromNodeId) return pathway.source;
  return null;
}

interface BfsEntry {
  nodeId: string;
  ring: number;
  /** All shortest paths from the struck node to this node, each as a list of pathway IDs */
  paths: string[][];
}

/**
 * Internal result from a single BFS+transmutation pass (no chain reactions).
 */
interface InternalStrikeResult {
  newBoard: BoardState;
  transmutations: Transmutation[];
  traversedPathwayIds: Set<string>;
}

/**
 * Core BFS + transmutation logic for a single strike (no chain reactions).
 * Used by both the main `strike()` and chain reaction processing.
 */
function strikeInternal(board: BoardState, nodeId: string): InternalStrikeResult {
  const sourceNode = board.nodes.find((n) => n.id === nodeId);

  // Node not found or locked: return board unchanged
  if (!sourceNode || sourceNode.locked) {
    return {
      newBoard: structuredClone(board),
      transmutations: [],
      traversedPathwayIds: new Set(),
    };
  }

  // Build adjacency information for BFS
  const nodeMap = new Map<string, Node>();
  for (const node of board.nodes) {
    nodeMap.set(node.id, node);
  }

  // BFS to compute ring distances and shortest paths
  const visited = new Map<string, BfsEntry>();
  const queue: BfsEntry[] = [{ nodeId, ring: 0, paths: [[]] }];
  visited.set(nodeId, { nodeId, ring: 0, paths: [[]] });

  // Track all pathways traversed during BFS
  const traversedPathwayIds = new Set<string>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) break;

    if (current.ring >= 3) continue;

    for (const pathway of board.pathways) {
      if (!canTraverse(pathway, current.nodeId, getOtherNode(pathway, current.nodeId) ?? '')) {
        continue;
      }

      const neighborId = getOtherNode(pathway, current.nodeId);
      if (neighborId === null) continue;
      if (!nodeMap.has(neighborId)) continue;

      const newRing = current.ring + 1;
      const newPaths = current.paths.map((p) => [...p, pathway.id]);

      const existingEntry = visited.get(neighborId);
      if (!existingEntry) {
        const entry: BfsEntry = { nodeId: neighborId, ring: newRing, paths: newPaths };
        visited.set(neighborId, entry);
        queue.push(entry);
        traversedPathwayIds.add(pathway.id);
      } else if (existingEntry.ring === newRing) {
        existingEntry.paths.push(...newPaths);
        traversedPathwayIds.add(pathway.id);
      }
    }
  }

  // Compute transmutations using PRE-STRIKE board state
  const transmutations: Transmutation[] = [];

  // Build a pathway lookup
  const pathwayMap = new Map<string, Pathway>();
  for (const pathway of board.pathways) {
    pathwayMap.set(pathway.id, pathway);
  }

  for (const [visitedNodeId, entry] of visited) {
    if (visitedNodeId === nodeId) continue;
    if (entry.ring < 1 || entry.ring > 3) continue;

    const targetNode = nodeMap.get(visitedNodeId);
    if (!targetNode) continue;
    if (targetNode.locked) continue;

    const ringNumber = entry.ring;

    let totalDelta = 0;

    for (const path of entry.paths) {
      const lastPathwayId = path[path.length - 1];
      if (!lastPathwayId) continue;

      const lastPathway = pathwayMap.get(lastPathwayId);
      if (!lastPathway) continue;

      let penultimateNodeId = nodeId;
      for (let i = 0; i < path.length - 1; i++) {
        const pathId = path[i];
        if (!pathId) break;
        const pw = pathwayMap.get(pathId);
        if (!pw) break;
        const nextNode = getOtherNode(pw, penultimateNodeId);
        if (!nextNode) break;
        penultimateNodeId = nextNode;
      }

      const penultimateNode = nodeMap.get(penultimateNodeId);
      if (!penultimateNode) continue;

      const pathwayModifier = getPathwayModifier(lastPathway, penultimateNode, targetNode);
      const rawDelta = ringNumber * pathwayModifier;

      const delta = lastPathway.type === PathwayType.Dampening
        ? Math.floor(rawDelta)
        : rawDelta;

      totalDelta += delta;
    }

    if (totalDelta === 0) continue;

    const oldFrequency = targetNode.frequency;
    const newFrequency = wrapFrequency(oldFrequency + totalDelta);

    const representativePathwayId = entry.paths[0]?.[entry.paths[0].length - 1] ?? '';

    transmutations.push({
      nodeId: visitedNodeId,
      oldFrequency,
      newFrequency,
      ring: ringNumber,
      pathwayId: representativePathwayId,
    });
  }

  // Apply all transmutations to produce new BoardState
  const newBoard = structuredClone(board);
  for (const t of transmutations) {
    const node = newBoard.nodes.find((n) => n.id === t.nodeId);
    if (node) {
      node.frequency = t.newFrequency;
    }
  }

  return {
    newBoard,
    transmutations,
    traversedPathwayIds,
  };
}

/**
 * Applies dynamic pathway changes: for each traversed pathway that has a
 * `dynamicSuccessor`, change its type to the successor value.
 * Returns the updated board and the list of pathway changes.
 */
function applyDynamicPathways(
  board: BoardState,
  traversedPathwayIds: Set<string>,
): { updatedBoard: BoardState; pathwayChanges: PathwayChange[] } {
  const pathwayChanges: PathwayChange[] = [];

  // Check if any traversed pathway has a dynamicSuccessor
  let hasDynamic = false;
  for (const pathway of board.pathways) {
    if (traversedPathwayIds.has(pathway.id) && pathway.dynamicSuccessor !== undefined) {
      hasDynamic = true;
      break;
    }
  }

  if (!hasDynamic) {
    return { updatedBoard: board, pathwayChanges };
  }

  const updatedBoard = structuredClone(board);
  for (const pathway of updatedBoard.pathways) {
    if (traversedPathwayIds.has(pathway.id) && pathway.dynamicSuccessor !== undefined) {
      const oldType = pathway.type;
      const newType = pathway.dynamicSuccessor;
      pathway.type = newType;
      pathwayChanges.push({
        pathwayId: pathway.id,
        oldType,
        newType,
      });
    }
  }

  return { updatedBoard, pathwayChanges };
}

/**
 * Processes a strike on the given node, propagating energy outward in BFS rings.
 * Energy transmutes node frequencies based on ring distance and pathway type.
 * All transmutations use the PRE-STRIKE board state (simultaneous resolution).
 *
 * After the initial strike, checks for chain reactions (resonator triggers)
 * and applies dynamic pathway changes.
 */
export function strike(board: BoardState, nodeId: string): StrikeResult {
  const sourceNode = board.nodes.find((n) => n.id === nodeId);

  // Node not found or locked: return board unchanged
  if (!sourceNode || sourceNode.locked) {
    return {
      newBoard: structuredClone(board),
      transmutations: [],
      struckNodeId: nodeId,
      chainSteps: [],
      pathwayChanges: [],
    };
  }

  // Perform the initial strike
  const initialResult = strikeInternal(board, nodeId);
  const transmutations = initialResult.transmutations;

  // Apply dynamic pathway changes (before chain reactions)
  const { updatedBoard: boardAfterDynamic, pathwayChanges } = applyDynamicPathways(
    initialResult.newBoard,
    initialResult.traversedPathwayIds,
  );

  // Chain reaction phase
  const chainSteps: ChainStep[] = [];
  const firedResonators = new Set<string>();
  let currentBoard = boardAfterDynamic;
  const MAX_CHAIN_DEPTH = 10;

  for (let depth = 0; depth < MAX_CHAIN_DEPTH; depth++) {
    // Find triggered resonators: nodes with resonator config whose frequency
    // now equals the trigger frequency, that haven't already fired, and aren't locked
    const triggered = currentBoard.nodes.filter(n =>
      n.resonator &&
      n.frequency === n.resonator.triggerFrequency &&
      !firedResonators.has(n.id) &&
      !n.locked
    );

    if (triggered.length === 0) break;

    for (const resonatorNode of triggered) {
      firedResonators.add(resonatorNode.id);

      // Fire a secondary strike from this resonator
      const chainResult = strikeInternal(currentBoard, resonatorNode.id);

      chainSteps.push({
        resonatorNodeId: resonatorNode.id,
        transmutations: chainResult.transmutations,
      });

      // Apply dynamic pathway changes from chain reaction strikes too
      const { updatedBoard: boardAfterChainDynamic, pathwayChanges: chainPathwayChanges } =
        applyDynamicPathways(chainResult.newBoard, chainResult.traversedPathwayIds);

      currentBoard = boardAfterChainDynamic;
      pathwayChanges.push(...chainPathwayChanges);
    }
  }

  return {
    newBoard: currentBoard,
    transmutations,
    struckNodeId: nodeId,
    chainSteps,
    pathwayChanges,
  };
}
